import React, { useState, useEffect } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { styled } from '@mui/system';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';
import 'react-datepicker/dist/react-datepicker.css';
import {
    addYears,
    differenceInDays,
    isWithinInterval,
    eachDayOfInterval,
    format,
} from 'date-fns';
import { getFeeBreakdown } from '../services/api';


const CalendarWrapper = styled(Box)({
    '& .react-datepicker': {
        fontFamily: 'Filson Soft, Arial, sans-serif',
        boxShadow: '0 3px 5px 2px rgba(0, 0, 0, .1)',
        borderRadius: '8px',
        width: '100%',
    },
    '& .react-datepicker__month-container': {
        width: '100%',
    },
    '& .react-datepicker__header': {
        backgroundColor: '#ffffff',
        borderBottom: 'none',
        padding: '0.5rem 0',
    },
    '& .react-datepicker__day--selected, & .react-datepicker__day--keyboard-selected': {
        backgroundColor: '#4CAF50',
        color: 'white',
    },
    '& .react-datepicker__current-month': {
        fontWeight: 500,
        fontSize: '1rem',
    },
    '& .react-datepicker__day': {
        fontSize: '0.875rem',
        lineHeight: '1.5rem',
        color: '#333',
    },
    '& .react-datepicker__day:hover': {
        backgroundColor: '#e0e0e0',
    },
    '& .react-datepicker__day--disabled': {
        color: '#ccc',
        opacity: 0.5,
        pointerEvents: 'none',
    },
    '& .react-datepicker__day--outside-month': {
        color: '#000',
        opacity: 1,
    },
    '& .react-datepicker__day--in-range': {
        backgroundColor: '#4CAF50',
        color: 'white',
    },
    '& .react-datepicker__day--in-range:hover': {
        backgroundColor: '#45a049',
        color: 'white',
    },
    '& .react-datepicker__day--selected': {
        backgroundColor: '#4CAF50',
        color: 'white',
    },
    '& .react-datepicker__day--keyboard-selected': {
        backgroundColor: '#4CAF50',
        color: 'white',
    },
    '& .react-datepicker__day-name': {
        color: '#4CAF50',
    },
});


const PriceBreakdownContainer = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[50],
}));

const PriceRow = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
});

const BookingSummary = ({
    listingId,
    pricePerDay,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    blockedPeriods = [],
}) => {
    const [feeBreakdown, setFeeBreakdown] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const isDateBlocked = (date) => {
        if (!date || !Array.isArray(blockedPeriods)) return false;
        return blockedPeriods.some((period) => {
            try {
                const start = new Date(period.start);
                const end = new Date(period.end);
                return isWithinInterval(date, { start, end });
            } catch (e) {
                console.error('Error processing blocked period:', e);
                return false;
            }
        });
    };


    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
        setError(null);
    };

    useEffect(() => {
        const fetchFeeBreakdown = async () => {
            if (startDate && endDate && listingId) {
                setIsLoading(true);
                setError(null);
                setFeeBreakdown(null);
                try {
                    const days = differenceInDays(endDate, startDate) + 1;
                    const response = await getFeeBreakdown(listingId, days);

                    if (response?.data) {
                        setFeeBreakdown(response.data);
                    } else {
                        throw new Error('Invalid fee breakdown response');
                    }
                } catch (error) {
                    console.error('Error fetching fee breakdown:', error);
                    setError('Unable to calculate fees. Please try again.');
                } finally {
                    setIsLoading(false);
                }
            } else {
                setFeeBreakdown(null);
            }
        };

        fetchFeeBreakdown();
    }, [startDate, endDate, listingId]);

    const renderPriceBreakdown = () => {
        if (error) {
            return (
                <Typography color="error" variant="body2" textAlign="center">
                    {error}
                </Typography>
            );
        }

        if (isLoading) {
            return (
                <PriceBreakdownContainer>
                    <Skeleton animation="wave" height={24} />
                    <Skeleton animation="wave" height={24} />
                    <Skeleton animation="wave" height={24} />
                </PriceBreakdownContainer>
            );
        }

        if (!feeBreakdown) return null;

        const basePrice = feeBreakdown.item_cost;
        const platformFee = feeBreakdown.platform_fee;

        return (
            <PriceBreakdownContainer>
                <PriceRow>
                    <Typography variant="body2">Base price</Typography>
                    <Typography variant="body2">{basePrice}</Typography>
                </PriceRow>
                {feeBreakdown.platform_fee !== undefined && (
                    <PriceRow>
                        <Typography variant="body2">Platform fee</Typography>
                        <Typography variant="body2">
                            {platformFee}
                        </Typography>
                    </PriceRow>
                )}
                {feeBreakdown.total_amount !== undefined && (
                    <PriceRow
                        sx={{
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            marginTop: 1,
                            paddingTop: 1,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight="bold">
                            Total
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {feeBreakdown.total_amount}
                        </Typography>
                    </PriceRow>
                )}
            </PriceBreakdownContainer>
        );
    };

    const safeParseDates = (periods) => {
        if (!Array.isArray(periods)) return [];
        return periods.reduce((acc, period) => {
            try {
                if (period?.start && period?.end) {
                    const start = new Date(period.start);
                    const end = new Date(period.end);
                    if (!isNaN(start) && !isNaN(end)) {
                        return [...acc, { start, end }];
                    }
                }
                return acc;
            } catch (e) {
                console.error('Error parsing blocked period:', e);
                return acc;
            }
        }, []);
    };

    return (
        <Box>
            <CalendarWrapper>
                <DatePicker
                    onChange={handleDateChange}
                    startDate={startDate}
                    endDate={endDate}
                    selectsRange
                    inline
                    placeholderText="Select dates"
                    minDate={new Date()}
                    maxDate={addYears(new Date(), 1)}
                    monthsShown={1}
                    excludeDates={safeParseDates(blockedPeriods).flatMap((period) =>
                        eachDayOfInterval({ start: period.start, end: period.end })
                    )}
                    filterDate={(date) => !isDateBlocked(date)}
                />
            </CalendarWrapper>

            {startDate && endDate ? (
                <Box mt={2}>
                    <Typography variant="body2" gutterBottom>
                        Pickup: {format(startDate, 'MMM d, yyyy')}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        Drop off: {format(endDate, 'MMM d, yyyy')}
                    </Typography>
                    {renderPriceBreakdown()}
                </Box>
            ) : (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    mt={2}
                    textAlign="center"
                >
                    Select dates to see total price
                </Typography>
            )}
        </Box>
    );
};

BookingSummary.propTypes = {
    listingId: PropTypes.number.isRequired,
    pricePerDay: PropTypes.number.isRequired,
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    setStartDate: PropTypes.func.isRequired,
    setEndDate: PropTypes.func.isRequired,
    blockedPeriods: PropTypes.arrayOf(
        PropTypes.shape({
            start: PropTypes.instanceOf(Date).isRequired,
            end: PropTypes.instanceOf(Date).isRequired,
        })
    ),
};

export default BookingSummary;